import React, { useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import useUser from '../../hooks/useUser';
import useCheckoutRedirect from '../../hooks/useCheckoutRedirect';
import useCourseCatalog from '../../hooks/useCourseCatalog';
import { pushEvent, ANALYTICS_EVENTS } from '../../utils/analytics';
import Reveal from '../../components/course/Reveal';
import StarRating from '../../components/course/StarRating';
import LogoRow from '../../components/course/LogoRow';
import FactStrip from '../../components/course/FactStrip';
import PreviewVideo from '../../components/course/PreviewVideo';
import GuaranteeBlock from '../../components/course/GuaranteeBlock';
import ScrollProgress from '../../components/course/ScrollProgress';
import { ROB_STORY, NO_CODING_BADGE, GUARANTEE_DAYS } from '../../components/course/courseContent';
import './CoursePage.css';

export const LIVE_COURSE_PATH = '/courses/ai-trading-bootcamp';

/**
 * Self-paced sales page.
 *
 * Deliberately omits the claims the old page made that production cannot yet
 * honour (30 days of Pro, upgrade credit toward a live seat). Nothing is
 * promised here that a buyer cannot actually receive today.
 */

const MODULES = [
  { name: 'Your AI trading workspace', detail: 'Install and configure Claude Code and Codex, and connect market data.' },
  { name: 'Research that cites its sources', detail: 'Force evidence, catch invented sources, build a repeatable routine.' },
  { name: 'Reading filings, earnings and news', detail: 'Get through the material fast without losing what matters.' },
  { name: 'Connecting your broker', detail: 'Schwab, Alpaca, Tradier, Interactive Brokers, Coinbase and Kraken.' },
  { name: 'Preparing and reviewing a trade', detail: 'Position sizing, risk checks and an approval step you control.' },
  { name: 'Options and crypto workflows', detail: 'Structured, defined-risk analysis across other markets.' },
  { name: 'Monitoring what changed', detail: 'Track a thesis and get told when the facts move, not just the price.' },
  { name: 'Automation, and when to use it', detail: 'Where automation genuinely helps, with guardrails and kill switches.' },
];

const COMPARISON = [
  { feature: 'All course lessons', selfPaced: true, live: true },
  { feature: 'Watch on your own schedule', selfPaced: true, live: true },
  { feature: 'Session recordings kept in your portal', selfPaced: true, live: true },
  { feature: 'Live sessions with Rob', selfPaced: false, live: true },
  { feature: 'Ask your own questions in class', selfPaced: false, live: true },
  { feature: 'Direct feedback on your setup', selfPaced: false, live: true },
  { feature: 'Cohort leaderboard', selfPaced: false, live: true },
  { feature: `${GUARANTEE_DAYS}-day money-back guarantee`, selfPaced: true, live: true },
];

const FAQS = [
  { q: 'How is this different from the live bootcamp?', a: 'Same lessons, no live room. You get the recordings and materials and work through them on your own schedule. You do not get live Q&A or feedback on your own setup.' },
  { q: 'How long do I have access?', a: 'The lessons stay in your course portal and you can return to them whenever you want.' },
  { q: 'Do I need to know how to code?', a: 'No. The whole point is directing Claude Code and Codex in plain English.' },
  { q: 'Can I move up to the live cohort later?', a: 'Yes. Join any future cohort at the price advertised at the time. Talk to Rob if you want to discuss it.' },
  { q: 'What if it is not for me?', a: `Ask for a refund within ${GUARANTEE_DAYS} days and you get your money back. No forms and no argument.` },
];

const CourseSelfPacedPage = () => {
  const { user } = useUser();
  const { startCheckout } = useCheckoutRedirect('course_self_paced');
  const { isLoading, selfPaced, upfrontPlan, pricingError } = useCourseCatalog();

  const track = useCallback((event, extra = {}) => {
    pushEvent({
      event, source: window.location.pathname,
      userId: user?.id || user?.sub, email: user?.email, ...extra,
    });
  }, [user]);

  const buy = useCallback((ctaLocation) => {
    track(ANALYTICS_EVENTS.BOOTCAMP_CHECKOUT_STARTED, { cta_location: ctaLocation, planKey: 'self-paced' });
    return startCheckout({
      products: [{ productSlug: 'ai-bootcamp-self-paced', planKey: selfPaced?.planKey || null }],
      flowContext: 'course_self_paced',
      metadata: {
        origin: {
          flow: 'course_self_paced', ctaLocation,
          returnUrl: '/ai-bot-builder-bootcamp/watch?welcome=1',
        },
      },
      contact: { email: user?.email || '', name: user?.name || '' },
    });
  }, [startCheckout, track, selfPaced, user]);

  const facts = [
    { number: MODULES.length, label: 'Modules' },
    { value: 'Start today', label: 'Availability' },
    { value: 'Your pace', label: 'Schedule' },
    { value: `${GUARANTEE_DAYS} days`, label: 'Money-back guarantee' },
  ];

  return (
    <div className="course-page" data-testid="self-paced-page">
      <ScrollProgress />
      <Helmet>
        <title>Self-Paced AI Trading Bootcamp | BotSpot</title>
        <meta
          name="description"
          content="Every lesson from the AI Trading Bootcamp, on your own schedule. Learn to research, verify and place reviewed trades with Claude Code and Codex."
        />
      </Helmet>

      <header className="cp-hero">
        <div className="cp-hero-copy">
          <Reveal><p className="cp-eyebrow">Self-paced · Start today</p></Reveal>
          <Reveal delay={60}><h1>The Same Bootcamp. Your Schedule.</h1></Reveal>
          <Reveal delay={120}>
            <p className="cp-sub">
              Every lesson from the AI Trading Bootcamp, available the moment you enroll. Learn to
              command Claude Code and Codex to research the market, verify what is true, and prepare
              trades through your own broker.
            </p>
          </Reveal>
          <Reveal delay={180}><div className="cp-hero-rating"><StarRating rating={4.6} /></div></Reveal>

          <Reveal delay={240}>
            {/* Never shows an invented price. */}
            {pricingError || !selfPaced ? (
              <p className="cp-price-error" data-testid="pricing-error" role="alert">
                Pricing is temporarily unavailable. Please refresh, or contact us and we will help you enroll.
              </p>
            ) : (
              <div className="cp-hero-price" data-testid="self-paced-price">
                <span className="cp-price-now">{selfPaced.display}</span>
                <span className="cp-price-unit">one-time</span>
              </div>
            )}
          </Reveal>

          <Reveal delay={300}>
            <div className="cp-hero-cta">
              <button type="button" className="cp-btn-primary" data-testid="buy-self-paced"
                onClick={() => buy('hero')} disabled={isLoading || Boolean(pricingError) || !selfPaced}>
                Get the self-paced course
              </button>
              <Link className="cp-btn-ghost" to={LIVE_COURSE_PATH}>See the live cohort</Link>
            </div>
          </Reveal>
        </div>

        <Reveal delay={160} className="cp-hero-art">
          <img src="/images/courses/ai-trading-workspace-hero.webp"
            alt="An AI trading workspace: assistant research on the left, price chart and a trade review card on the right"
            width="1280" height="714" loading="eager" />
        </Reveal>
      </header>

      <LogoRow />
      <FactStrip facts={facts} />
      <PreviewVideo blurb="This is Rob teaching in a live session. The recordings you get are exactly this." />

      <section className="cp-section cp-story">
        <Reveal><h2>{ROB_STORY.heading}</h2></Reveal>
        <Reveal delay={70}><p>{ROB_STORY.paragraphs[0]}</p></Reveal>
        <Reveal delay={140}><p>{ROB_STORY.paragraphs[2]}</p></Reveal>
      </section>

      <section className="cp-section">
        <Reveal><h2>What is inside</h2></Reveal>
        <ul className="cp-modules">
          {MODULES.map((m, i) => (
            <Reveal as="li" key={m.name} delay={i * 45}>
              <span className="cp-module-name">{m.name}</span>
              <span className="cp-module-len">{m.detail}</span>
            </Reveal>
          ))}
        </ul>
      </section>

      <section className="cp-section">
        <Reveal><h2>Self-paced or live?</h2></Reveal>
        <Reveal delay={80}>
          <div className="cp-compare-wrap">
            <table className="cp-compare">
              <thead>
                <tr>
                  <th scope="col">What you get</th>
                  <th scope="col">Self-paced</th>
                  <th scope="col" className="cp-col-live">Live cohort <span>most popular</span></th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map((row) => (
                  <tr key={row.feature}>
                    <td>{row.feature}</td>
                    <td>{row.selfPaced ? <span className="cp-yes">Yes</span> : <span className="cp-no">No</span>}</td>
                    <td className="cp-col-live">{row.live ? <span className="cp-yes">Yes</span> : <span className="cp-no">No</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>
        <Reveal delay={140}>
          <p className="cp-note">
            If you can attend on the session nights, the live cohort is worth it: you get your own
            questions answered and your setup reviewed.{' '}
            <Link to={LIVE_COURSE_PATH}>
              See the live cohort{upfrontPlan?.display ? ` from ${upfrontPlan.display}` : ''}
            </Link>
          </p>
        </Reveal>
      </section>

      <section className="cp-section cp-fit">
        <Reveal>
          <div>
            <h3>Self-paced is right if</h3>
            <ul>
              <li>The live session times do not work for you</li>
              <li>You are in a timezone where evenings Eastern are the middle of the night</li>
              <li>You prefer working through material on your own</li>
            </ul>
          </div>
        </Reveal>
        <Reveal delay={100}>
          <div>
            <h3>Choose live instead if</h3>
            <ul>
              <li>You want your own questions answered as they come up</li>
              <li>You want Rob to look at your actual setup</li>
              <li>You know you finish things when other people are expecting you</li>
            </ul>
          </div>
        </Reveal>
      </section>

      <section className="cp-section cp-pricing">
        <Reveal><h2>Get started</h2></Reveal>
        <Reveal delay={80}>
          <div className="cp-pricing-card">
            {pricingError || !selfPaced ? (
              <p className="cp-price-error" role="alert">
                We could not load current pricing. Rather than show you a price that might be wrong,
                we would rather say so. Please refresh, or contact us and we will get you enrolled.
              </p>
            ) : (
              <div className="cp-hero-price" style={{ justifyContent: 'center' }}>
                <span className="cp-price-now">{selfPaced.display}</span>
                <span className="cp-price-unit">one-time</span>
              </div>
            )}
            <button type="button" className="cp-btn-primary cp-btn-block"
              onClick={() => buy('pricing')} disabled={Boolean(pricingError) || !selfPaced}>
              Get the self-paced course
            </button>
            <p className="ck-nocode cp-nocode-cta">{NO_CODING_BADGE}</p>
            <p className="cp-pricing-links">
              <Link to={LIVE_COURSE_PATH}>Prefer live sessions? See the cohort</Link>
            </p>
          </div>
        </Reveal>
      </section>

      <section className="cp-section cp-guarantee-section">
        <Reveal><GuaranteeBlock /></Reveal>
      </section>

      <section className="cp-section cp-faq">
        <Reveal><h2>Questions people ask</h2></Reveal>
        <dl>
          {FAQS.map((f, i) => (
            <Reveal as="div" key={f.q} delay={i * 40}><dt>{f.q}</dt><dd>{f.a}</dd></Reveal>
          ))}
        </dl>
      </section>

      <div className="cp-sticky">
        <span>{selfPaced ? `${selfPaced.display} one-time` : 'Pricing unavailable'}</span>
        <button type="button" onClick={() => buy('sticky')} disabled={Boolean(pricingError) || !selfPaced}>
          Get the course
        </button>
      </div>

      <p className="cp-disclaimer">
        BotSpot is not an investment advisor. This is educational material about using AI tools in your
        own research and trading process. Nothing here is a recommendation to buy or sell any security,
        and no outcome or return is promised.
      </p>
    </div>
  );
};

export default CourseSelfPacedPage;
